import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Box,
    IconButton,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./DeletePostModal.module.scss";

interface DeletePostModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isDeleting: boolean;
}

const CONFIRMATION_TEXT = "delete";

const DeletePostModal: React.FC<DeletePostModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isDeleting,
}) => {
    const [confirmationInput, setConfirmationInput] = useState("");

    // Reset input when modal is closed
    useEffect(() => {
        if (!isOpen) {
            setConfirmationInput("");
        }
    }, [isOpen]);

    const isConfirmationTextMatched = confirmationInput.toLowerCase() === CONFIRMATION_TEXT;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{ className: styles.dialogPaper }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle className={styles.dialogTitle}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DeleteForeverIcon sx={{ color: "var(--error-500)" }} />
                    Delete Your Post
                </Box>
                <IconButton onClick={onClose} className={styles.closeButton}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
                <DialogContentText className={styles.dialogText}>
                    Are you sure you want to delete your post from Aspire? This action is
                    irreversible and will permanently remove your post and all associated content.
                </DialogContentText>
                <DialogContentText className={styles.dialogInstructions}>
                    To confirm, please type &ldquo;<b>{CONFIRMATION_TEXT}</b>&rdquo; in the box
                    below.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="delete-confirmation"
                    label={`Type "${CONFIRMATION_TEXT}" to confirm`}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={confirmationInput}
                    onChange={(e) => setConfirmationInput(e.target.value)}
                    disabled={isDeleting}
                    className={styles.confirmationInput}
                />
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
                <Button onClick={onClose} disabled={isDeleting} className={styles.cancelButton}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={!isConfirmationTextMatched || isDeleting}
                    className={styles.deleteButton}
                    variant="contained"
                    startIcon={
                        isDeleting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <DeleteForeverIcon />
                        )
                    }
                >
                    {isDeleting ? "Deleting..." : "Delete Post"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeletePostModal;
